import { doc, getDoc, serverTimestamp, setDoc } from "firebase/firestore";
import { getFirebaseDb, isFirebaseConfigured } from "./firebaseApp";

const DAILY_VERSION = 2;
const LOGIN_REWARD_MARKS = 20;
const DAILY_CHAIN_COUNT = 3;
const DAILY_SINGLE_COUNT = 4;

const CHAIN_LIBRARY = [
	{
		familyId: "tower_builds",
		category: "Build",
		trackKey: "tower_builds",
		tiers: [
			{ target: 5, reward: 10, label: "Deploy 5 towers", description: "Open the lane fast" },
			{ target: 10, reward: 14, label: "Deploy 10 more towers", description: "Expand the grid" },
			{ target: 18, reward: 18, label: "Deploy 18 more towers", description: "Flood the sector with firepower" },
		],
	},
	{
		familyId: "kill_count",
		category: "Combat",
		trackKey: "kill_count",
		tiers: [
			{ target: 30, reward: 10, label: "Destroy 30 hostiles", description: "Thin the first wave" },
			{ target: 65, reward: 14, label: "Destroy 65 more hostiles", description: "Keep the lane clear" },
			{ target: 110, reward: 18, label: "Destroy 110 more hostiles", description: "Break the invasion line" },
		],
	},
	{
		familyId: "wave_count",
		category: "Control",
		trackKey: "wave_count",
		tiers: [
			{ target: 4, reward: 10, label: "Clear 4 waves", description: "Stabilize the sector" },
			{ target: 8, reward: 14, label: "Clear 8 more waves", description: "Hold through another cycle" },
			{ target: 13, reward: 18, label: "Clear 13 more waves", description: "Outlast the long push" },
		],
	},
	{
		familyId: "score_gain",
		category: "Score",
		trackKey: "score_gain",
		tiers: [
			{ target: 700, reward: 12, label: "Earn 700 score", description: "Get the run moving" },
			{ target: 1600, reward: 16, label: "Earn 1,600 more score", description: "Climb the board" },
			{ target: 3000, reward: 22, label: "Earn 3,000 more score", description: "Post a headline score" },
		],
	},
	{
		familyId: "survival_waves",
		category: "Survival",
		trackKey: "survival_waves",
		tiers: [
			{ target: 3, reward: 12, label: "Clear 3 survival waves", description: "Test the fallback line" },
			{ target: 7, reward: 16, label: "Clear 7 more survival waves", description: "Keep endless under control" },
			{ target: 12, reward: 20, label: "Clear 12 more survival waves", description: "Outlast the siege" },
		],
	},
	{
		familyId: "gold_spent",
		category: "Economy",
		trackKey: "gold_spent",
		tiers: [
			{ target: 450, reward: 10, label: "Spend 450 gold", description: "Spend, don’t hoard" },
			{ target: 1100, reward: 15, label: "Spend 1,100 more gold", description: "Scale the buildout" },
			{ target: 2200, reward: 20, label: "Spend 2,200 more gold", description: "Commit to expansion" },
		],
	},
	{
		familyId: "tower_upgrades",
		category: "Upgrade",
		trackKey: "tower_upgrades",
		tiers: [
			{ target: 3, reward: 10, label: "Upgrade 3 towers", description: "Power up the front line" },
			{ target: 6, reward: 14, label: "Upgrade 6 more towers", description: "Refit for heavier traffic" },
			{ target: 10, reward: 18, label: "Upgrade 10 more towers", description: "Push into veteran spec" },
		],
	},
	{
		familyId: "boss_kills",
		category: "Elite",
		trackKey: "boss_kills",
		tiers: [
			{ target: 1, reward: 14, label: "Eliminate 1 boss", description: "Drop the first elite" },
			{ target: 2, reward: 18, label: "Eliminate 2 more bosses", description: "Handle another elite wave" },
			{ target: 3, reward: 24, label: "Eliminate 3 more bosses", description: "Crush the command ships" },
		],
	},
];

const SINGLE_LIBRARY = [
	{ templateId: "campaign_push", category: "Campaign", trackKey: "campaign_maps", target: 1, reward: 18, label: "Secure 1 campaign map", description: "Take one campaign sector" },
	{ templateId: "campaign_push_plus", category: "Campaign", trackKey: "campaign_maps", target: 2, reward: 26, label: "Secure 2 campaign maps", description: "Chain two victories" },
	{ templateId: "field_resale", category: "Economy", trackKey: "towers_sold", target: 2, reward: 9, label: "Sell 2 towers", description: "Recycle the line" },
	{ templateId: "field_resale_plus", category: "Economy", trackKey: "towers_sold", target: 4, reward: 13, label: "Sell 4 towers", description: "Rebuild from scratch" },
	{ templateId: "upgrade_push", category: "Upgrade", trackKey: "tower_upgrades", target: 8, reward: 18, label: "Upgrade 8 towers", description: "Tune the whole formation" },
	{ templateId: "scoring_run", category: "Score", trackKey: "score_gain", target: 2400, reward: 18, label: "Earn 2,400 score", description: "Make it a leaderboard run" },
	{ templateId: "kill_screen", category: "Combat", trackKey: "kill_count", target: 140, reward: 20, label: "Destroy 140 hostiles", description: "Clear a heavy swarm" },
	{ templateId: "survival_shift", category: "Survival", trackKey: "survival_waves", target: 6, reward: 18, label: "Clear 6 survival waves", description: "Hold the endless line" },
	{ templateId: "economy_burn", category: "Economy", trackKey: "gold_spent", target: 1500, reward: 18, label: "Spend 1,500 gold", description: "Keep credits moving" },
	{ templateId: "tower_line", category: "Build", trackKey: "tower_builds", target: 14, reward: 16, label: "Deploy 14 towers", description: "Pack the grid" },
	{ templateId: "wave_marathon", category: "Control", trackKey: "wave_count", target: 12, reward: 20, label: "Clear 12 waves", description: "Finish the long set" },
	{ templateId: "elite_hunt", category: "Elite", trackKey: "boss_kills", target: 2, reward: 24, label: "Eliminate 2 bosses", description: "Drop two elites" },
];

const CHAIN_MAP = new Map(CHAIN_LIBRARY.map((entry) => [entry.familyId, entry]));
const SINGLE_MAP = new Map(SINGLE_LIBRARY.map((entry) => [entry.templateId, entry]));
const dailyWriteQueue = new Map();

const requireDb = () => {
	const db = getFirebaseDb();
	if (!isFirebaseConfigured || !db) {
		throw new Error("Firestore is not configured. Add Firebase environment variables first.");
	}
	return db;
};

const queueDailyWrite = async (uid, operation) => {
	if (!uid) return operation();

	const previous = dailyWriteQueue.get(uid) || Promise.resolve();
	const next = previous
		.catch(() => {})
		.then(operation)
		.finally(() => {
			if (dailyWriteQueue.get(uid) === next) dailyWriteQueue.delete(uid);
		});

	dailyWriteQueue.set(uid, next);
	return next;
};

const getTodayKey = (date = new Date()) => {
	const year = date.getFullYear();
	const month = String(date.getMonth() + 1).padStart(2, "0");
	const day = String(date.getDate()).padStart(2, "0");
	return `${year}-${month}-${day}`;
};

const getYesterdayKey = (date = new Date()) => {
	const previous = new Date(date);
	previous.setDate(date.getDate() - 1);
	return getTodayKey(previous);
};

const hashDayKey = (dayKey) => [...dayKey].reduce((total, char, index) => total + char.charCodeAt(0) * (index + 7), 0);

const pickUniqueEntries = (pool, count, seed, getKey = (item) => item.familyId || item.templateId) => {
	const picks = [];
	const usedKeys = new Set();

	for (let index = 0; picks.length < count && index < pool.length * 4; index += 1) {
		const candidate = pool[(seed + index * 5) % pool.length];
		const key = getKey(candidate);
		if (usedKeys.has(key)) continue;
		usedKeys.add(key);
		picks.push(candidate);
	}

	return picks;
};

const buildChainObjective = (familyId, tierIndex = 0) => {
	const chain = CHAIN_MAP.get(familyId);
	const tier = chain?.tiers?.[tierIndex];
	if (!chain || !tier) return null;

	return {
		instanceId: `${familyId}:tier:${tierIndex + 1}`,
		familyId,
		kind: "chain",
		tier: tierIndex + 1,
		category: chain.category,
		trackKey: chain.trackKey,
		label: tier.label,
		description: tier.description,
		target: tier.target,
		progress: 0,
		completed: false,
		claimed: false,
		reward: tier.reward,
		replaceable: tierIndex < chain.tiers.length - 1,
		completedAt: null,
	};
};

const buildSingleObjective = (templateId) => {
	const template = SINGLE_MAP.get(templateId);
	if (!template) return null;

	return {
		instanceId: templateId,
		familyId: templateId,
		kind: "single",
		tier: 1,
		category: template.category,
		trackKey: template.trackKey,
		label: template.label,
		description: template.description,
		target: template.target,
		progress: 0,
		completed: false,
		claimed: false,
		reward: template.reward,
		replaceable: false,
		completedAt: null,
	};
};

const buildObjectiveLoadout = (dayKey) => {
	const seed = hashDayKey(dayKey);
	const chains = pickUniqueEntries(CHAIN_LIBRARY, DAILY_CHAIN_COUNT, seed).map((entry) => buildChainObjective(entry.familyId, 0));
	const singles = pickUniqueEntries(SINGLE_LIBRARY, DAILY_SINGLE_COUNT, seed * 3 + 17, (item) => item.templateId).map((entry) => buildSingleObjective(entry.templateId));

	return [...chains, ...singles].filter(Boolean);
};

const buildDailyState = (previousDaily, dayKey) => ({
	version: DAILY_VERSION,
	dayKey,
	loginStreak: previousDaily?.dayKey === getYesterdayKey() ? (previousDaily?.loginStreak || 0) + 1 : 1,
	loginReward: {
		id: "login",
		label: "Daily Login",
		description: "Check in and collect marks",
		reward: LOGIN_REWARD_MARKS,
		claimed: false,
	},
	activeObjectives: buildObjectiveLoadout(dayKey),
	completedObjectives: [],
	claimedCount: 0,
});

const normalizeLoginReward = (loginReward) => ({
	id: "login",
	label: loginReward?.label || "Daily Login",
	description: loginReward?.description || "Check in and collect marks",
	reward: Number(loginReward?.reward) || LOGIN_REWARD_MARKS,
	claimed: Boolean(loginReward?.claimed),
});

const normalizeObjective = (objective) => ({
	instanceId: objective?.instanceId || objective?.id || objective?.familyId || `legacy-${Math.random().toString(36).slice(2, 8)}`,
	familyId: objective?.familyId || objective?.id || objective?.templateId || "legacy",
	kind: objective?.kind || "single",
	tier: Number(objective?.tier) || 1,
	category: objective?.category || "Directive",
	trackKey: objective?.trackKey || "score_gain",
	label: objective?.label || "Daily Op",
	description: objective?.description || "Complete the objective",
	target: Number(objective?.target) || 1,
	progress: Number(objective?.progress) || 0,
	completed: Boolean(objective?.completed),
	claimed: Boolean(objective?.claimed),
	reward: Number(objective?.reward) || 0,
	replaceable: Boolean(objective?.replaceable),
	completedAt: objective?.completedAt ?? null,
});

const normalizeCompletedRecord = (record) => ({
	instanceId: record?.instanceId || record?.id || `claimed-${Math.random().toString(36).slice(2, 8)}`,
	label: record?.label || "Claimed Op",
	description: record?.description || "",
	reward: Number(record?.reward) || 0,
	category: record?.category || "Directive",
	kind: record?.kind || "single",
	tier: Number(record?.tier) || 1,
	claimedAt: Number(record?.claimedAt) || Date.now(),
});

const normalizeDailyState = (daily, dayKey) => {
	if (!daily || daily.dayKey !== dayKey || daily.version !== DAILY_VERSION || !Array.isArray(daily.activeObjectives)) {
		return buildDailyState(daily, dayKey);
	}
	return {
		version: DAILY_VERSION,
		dayKey,
		loginStreak: Number(daily.loginStreak) || 1,
		loginReward: normalizeLoginReward(daily.loginReward),
		activeObjectives: (daily.activeObjectives || []).map(normalizeObjective),
		completedObjectives: trimCompletedHistory((daily.completedObjectives || []).map(normalizeCompletedRecord)),
		claimedCount: Number(daily.claimedCount) || 0,
	};
};

const extractDailyProgress = (runProgress) => ({
	kill_count: runProgress.kills || 0,
	wave_count: runProgress.wavesCleared || 0,
	score_gain: runProgress.scoreGained || 0,
	tower_builds: runProgress.towersBuilt || 0,
	survival_waves: runProgress.survivalWaves || 0,
	campaign_maps: runProgress.campaignMaps || 0,
	gold_spent: runProgress.goldSpent || 0,
	tower_upgrades: runProgress.towersUpgraded || 0,
	towers_sold: runProgress.towersSold || 0,
	boss_kills: runProgress.bossesKilled || 0,
});

const updateObjectiveProgress = (objective, progressDelta) => {
	if (objective.claimed) return objective;

	const increment = progressDelta[objective.trackKey] || 0;
	if (!increment) return objective;

	const nextProgress = Math.min(objective.target, (objective.progress || 0) + increment);
	const justCompleted = nextProgress >= objective.target && !objective.completed;

	return {
		...objective,
		progress: nextProgress,
		completed: nextProgress >= objective.target,
		completedAt: justCompleted ? Date.now() : objective.completedAt || null,
	};
};

const createCompletedRecord = (objective) => ({
	instanceId: objective.instanceId,
	label: objective.label,
	description: objective.description,
	reward: objective.reward,
	category: objective.category,
	kind: objective.kind,
	tier: objective.tier,
	claimedAt: Date.now(),
});

const trimCompletedHistory = (history = []) => history.slice(0, 18);

export const getDailyClaimables = (daily) => {
	if (!daily) return [];

	const claimables = [];
	if (daily.loginReward && !daily.loginReward.claimed) {
		claimables.push({
			rewardId: "login",
			label: daily.loginReward.label,
			description: daily.loginReward.description,
			reward: daily.loginReward.reward,
			category: "Check-In",
			kind: "login",
			completedAt: null,
		});
	}

	for (const objective of daily.activeObjectives || []) {
		if (objective.completed && !objective.claimed) {
			claimables.push({
				rewardId: objective.instanceId,
				label: objective.label,
				description: objective.description,
				reward: objective.reward,
				category: objective.category,
				kind: objective.kind,
				completedAt: objective.completedAt || null,
			});
		}
	}

	return claimables.sort((left, right) => (right.completedAt || 0) - (left.completedAt || 0));
};

export const ensureDailyOps = async (user, profile) => {
	if (!isFirebaseConfigured || !user?.uid) return profile || null;

	return queueDailyWrite(user.uid, async () => {
		const db = requireDb();
		const ref = doc(db, "players", user.uid);
		const dayKey = getTodayKey();
		const snapshot = await getDoc(ref);
		const current = snapshot.exists() ? snapshot.data() : {};
		const daily = normalizeDailyState(current.daily, dayKey);

		const nextProfile = {
			...current,
			...profile,
			uid: user.uid,
			commandMarks: current.commandMarks || 0,
			daily,
		};

		await setDoc(ref, {
			daily,
			updatedAt: serverTimestamp(),
			lastSeenAt: Date.now(),
		}, { merge: true });

		return nextProfile;
	});
};

export const claimDailyReward = async ({ user, rewardId }) => {
	if (!isFirebaseConfigured || !user?.uid || !rewardId) return null;

	return queueDailyWrite(user.uid, async () => {
		const db = requireDb();
		const ref = doc(db, "players", user.uid);
		const dayKey = getTodayKey();
		const snapshot = await getDoc(ref);
		const current = snapshot.exists() ? snapshot.data() : {};
		const daily = normalizeDailyState(current.daily, dayKey);
		let commandMarks = current.commandMarks || 0;
		let activeObjectives = [...(daily.activeObjectives || [])];
		let completedObjectives = [...(daily.completedObjectives || [])];

		if (rewardId === "login") {
			if (daily.loginReward?.claimed) {
				return { ...current, commandMarks, daily };
			}

			commandMarks += daily.loginReward.reward || 0;
			daily.loginReward = {
				...daily.loginReward,
				claimed: true,
			};
		} else {
			const objectiveIndex = activeObjectives.findIndex((objective) => objective.instanceId === rewardId);
			if (objectiveIndex === -1) throw new Error("That Daily Op is no longer available.");

			const objective = activeObjectives[objectiveIndex];
			if (!objective.completed || objective.claimed) {
				throw new Error("That Daily Op is not ready to claim yet.");
			}

			commandMarks += objective.reward || 0;
			completedObjectives.unshift(createCompletedRecord(objective));

			if (objective.kind === "chain") {
				const replacement = buildChainObjective(objective.familyId, objective.tier);
				if (replacement) activeObjectives[objectiveIndex] = replacement;
				else activeObjectives.splice(objectiveIndex, 1);
			} else {
				activeObjectives.splice(objectiveIndex, 1);
			}
		}

		const nextDaily = {
			...daily,
			activeObjectives,
			completedObjectives: trimCompletedHistory(completedObjectives),
			claimedCount: (daily.claimedCount || 0) + 1,
		};

		const nextProfile = {
			...current,
			commandMarks,
			daily: nextDaily,
		};

		await setDoc(ref, {
			commandMarks,
			daily: nextDaily,
			updatedAt: serverTimestamp(),
			lastSeenAt: Date.now(),
		}, { merge: true });

		return nextProfile;
	});
};

export const syncDailyProgress = async (user, runProgress) => {
	if (!isFirebaseConfigured || !user?.uid) return null;

	const progressDelta = extractDailyProgress(runProgress);
	if (!Object.values(progressDelta).some(Boolean)) return null;

	return queueDailyWrite(user.uid, async () => {
		const db = requireDb();
		const ref = doc(db, "players", user.uid);
		const dayKey = getTodayKey();
		const snapshot = await getDoc(ref);
		const current = snapshot.exists() ? snapshot.data() : {};
		const daily = normalizeDailyState(current.daily, dayKey);
		const activeObjectives = (daily.activeObjectives || []).map((objective) => updateObjectiveProgress(objective, progressDelta));

		const nextDaily = {
			...daily,
			activeObjectives,
		};

		const nextProfile = {
			...current,
			commandMarks: current.commandMarks || 0,
			daily: nextDaily,
		};

		await setDoc(ref, {
			daily: nextDaily,
			updatedAt: serverTimestamp(),
		}, { merge: true });

		return nextProfile;
	});
};

export const loadDailyProfile = async (uid) => {
	if (!isFirebaseConfigured || !uid) return null;
	const snapshot = await getDoc(doc(requireDb(), "players", uid));
	return snapshot.exists() ? snapshot.data() : null;
};

export const getDailyDayKey = getTodayKey;

